'use client'

import * as React from 'react'
import { StudioLayoutContentInsetProvider } from '@repo/studio-components-studio-layout/content-inset'
import { useIsCompactViewport } from '@repo/studio-hooks-dom/use-breakpoint'
import { useAidaOverlayStore } from '../../stores/overlay-store'
import { useIsAidaAllowed } from '../../hooks/use-is-aida-allowed'
import { useClearAidaOnLogOut } from '../../hooks/use-clear-aida-on-log-out'
import { AidaFloatingButtonHost } from './aida-floating-button-host'
import { AidaOverlay } from './aida-overlay'
import './aida-cross-document.css'

// The layout shell reads the inset from context, so it still resizes. This skips everything above it.
const BesideDock = React.memo(function BesideDock({ children }: { children: React.ReactNode }) {
  return <div className="isolate">{children}</div>
})

function AidaOverlayHost() {
  const allowed = useIsAidaAllowed()
  if (!allowed) {
    return null
  }

  return <AidaOverlay />
}

function AidaStudioLayoutInset({
  children,
  applyInset,
}: {
  children: React.ReactNode
  applyInset: boolean
}) {
  const open = useAidaOverlayStore(state => state.open)
  const mode = useAidaOverlayStore(state => state.mode)
  const dockWidth = useAidaOverlayStore(state => state.dockWidth)
  const isCompact = useIsCompactViewport()
  const rightInsetPx = applyInset && open && mode === 'dock' && !isCompact ? dockWidth : 0

  return (
    <StudioLayoutContentInsetProvider rightInsetPx={rightInsetPx}>
      <BesideDock>{children}</BesideDock>
    </StudioLayoutContentInsetProvider>
  )
}

export function AidaDialogProvider({
  children,
  showAgent = true,
  ssrSafe = false,
}: {
  children: React.ReactNode
  showAgent?: boolean
  ssrSafe?: boolean
}) {
  useClearAidaOnLogOut()
  const [agentReady, setAgentReady] = React.useState(!ssrSafe)
  React.useEffect(() => {
    if (ssrSafe) {
      setAgentReady(true)
    }
  }, [ssrSafe])
  const showChrome = showAgent && agentReady
  return (
    <>
      <AidaStudioLayoutInset applyInset={showChrome}>{children}</AidaStudioLayoutInset>
      {showChrome ? (
        <>
          <AidaOverlayHost />
          <AidaFloatingButtonHost />
        </>
      ) : null}
    </>
  )
}
